import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Grid, Typography } from "@mui/material"

import type { InstallationType, InstallationDetailsForm } from "types/device.types"
import type { Powerline } from "types/powerlines.types"
import type { Span } from "types/spans.types"
import type { Tower } from "types/towers.types"

import ControlledInput from "widgets/common/ControlledInput"
import ConfirmationPopup from "widgets/common/ConfirmationPopup"
import { DETAILS_CONFIG } from "./config"
import SpansProvider from "./SpansProvider"
import {
  FIELD_SPLIT_THRESHOLD,
  MAX_FIELDS_IN_MIN_LAYOUT,
  getFormValues,
  handleFieldChange,
} from "./helpers"
import InstallationFormActions from "./InstallationFormActions"

interface EditInstallationDetailsProps {
  installationType: InstallationType
  defaultValues: InstallationDetailsForm
  deviceName: string
  isLoading?: boolean
  powerline?: Powerline | null
  span?: Span | null
  towerA?: Tower | null
  towerB?: Tower | null
  onClose: () => void
  onSubmit: (data: InstallationDetailsForm) => Promise<void>
}

const EditInstallationDetails = ({
  installationType,
  defaultValues,
  deviceName,
  isLoading = false,
  powerline,
  span,
  towerA,
  towerB,
  onClose,
  onSubmit,
}: EditInstallationDetailsProps) => {
  const { t } = useTranslation()
  const [isUninstallConfirmOpen, setIsUninstallConfirmOpen] = useState(false)
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false)

  // Convert display strings to actual IDs so autocomplete fields can match their options
  const formValues = useMemo(
    () => getFormValues(installationType, defaultValues, powerline, span, towerA, towerB),
    [defaultValues, installationType, powerline, span, towerA, towerB],
  )

  const methods = useForm<InstallationDetailsForm>({
    mode: "all",
    defaultValues: formValues,
  })

  useEffect(() => {
    methods.reset(formValues)
  }, [formValues, methods])

  const isHandlingChange = useRef(false)

  // Reset dependent fields on change
  useEffect(() => {
    const subscription = methods.watch((_, { name }) => {
      if (isHandlingChange.current) return
      isHandlingChange.current = true
      try {
        handleFieldChange(name, methods)
      } finally {
        isHandlingChange.current = false
      }
    })
    return () => subscription.unsubscribe()
  }, [methods])

  const watchedInstallationType = methods.watch("installationType") as InstallationType

  const getInitialInputData = useCallback(
    (fieldName: keyof InstallationDetailsForm): Powerline | Span | Tower | undefined => {
      const dataMap: Partial<
        Record<keyof InstallationDetailsForm, Powerline | Span | Tower | undefined>
      > = {
        powerlineId: powerline || undefined,
        towerId: towerA || undefined,
        towerAId: towerA || undefined,
        towerBId: towerB || undefined,
        spanId: span || undefined,
      }
      return dataMap[fieldName]
    },
    [powerline, span, towerA, towerB],
  )

  const fields = useMemo(() => {
    const activeType = watchedInstallationType || installationType
    const config = activeType ? DETAILS_CONFIG[activeType] : []
    return config.map((field) => (
      <Grid container columnSpacing={4} mb={2} alignItems="center" key={field.name}>
        <Grid item xs={12} sm={5}>
          <Typography>{t(field.labelKey, field.labelOptions)}</Typography>
        </Grid>
        <Grid item xs={12} sm={7}>
          {field.readOnly ? (
            <Typography color="text.disabled">
              {defaultValues[field.name] || "-"}
            </Typography>
          ) : field.customInput ? (
            <field.customInput
              name={field.name}
              initialData={getInitialInputData(field.name)}
              placeholder={field.placeholder ? t(field.placeholder) : undefined}
            />
          ) : (
            <ControlledInput
              styles={{ mb: 0 }}
              name={field.name}
              type={field.type || "text"}
              variant="outlined"
              size="small"
              placeholder=""
              selectOptions={field.selectOptions}
            />
          )}
        </Grid>
      </Grid>
    ))
  }, [watchedInstallationType, installationType, t, defaultValues, getInitialInputData])

  const handleSubmit = methods.handleSubmit(onSubmit)

  const handleUninstall = useCallback(() => {
    setIsUninstallConfirmOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    if (methods.formState.isDirty) {
      setIsDiscardConfirmOpen(true)
    } else {
      onClose()
    }
  }, [methods.formState.isDirty, onClose])

  const handleConfirmUninstall = useCallback(async () => {
    await onSubmit({ installationType: "uninstalled" } as InstallationDetailsForm)
    setIsUninstallConfirmOpen(false)
  }, [onSubmit])

  return (
    <form onSubmit={handleSubmit}>
      <FormProvider {...methods}>
        <SpansProvider>
          {fields.length > FIELD_SPLIT_THRESHOLD ? (
            <Grid container spacing={{ xs: 0, md: 4 }}>
              <Grid item xs={12} md={6}>
                {fields.slice(
                  0,
                  fields.length > MAX_FIELDS_IN_MIN_LAYOUT
                    ? Math.ceil(fields.length / 2)
                    : FIELD_SPLIT_THRESHOLD,
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {fields.slice(
                  fields.length > MAX_FIELDS_IN_MIN_LAYOUT
                    ? Math.ceil(fields.length / 2)
                    : FIELD_SPLIT_THRESHOLD,
                )}
              </Grid>
            </Grid>
          ) : (
            <Grid container>
              <Grid item xs={12} md={6}>
                {fields}
              </Grid>
            </Grid>
          )}
          <InstallationFormActions
            installationType={installationType}
            isLoading={isLoading}
            disabled={!methods.formState.isDirty}
            onClose={handleClose}
            onUninstall={handleUninstall}
          />
        </SpansProvider>
      </FormProvider>
      <ConfirmationPopup
        open={isUninstallConfirmOpen}
        onClose={() => setIsUninstallConfirmOpen(false)}
        description={t("device_installation.ARE_YOU_SURE_TO_UNINSTALL_DEVICE", {
          deviceName,
        })}
        mainButtonText={t("device_installation.UNINSTALL")}
        onConfirm={handleConfirmUninstall}
        isLoading={isLoading}
        isDeletePopup
      />
      <ConfirmationPopup
        open={isDiscardConfirmOpen}
        onClose={() => setIsDiscardConfirmOpen(false)}
        description={t("generic.ARE_YOU_SURE_DISCARD")}
        mainButtonText={t("generic.DISCARD_CHANGES")}
        onConfirm={onClose}
        isLoading={false}
        isDeletePopup
      />
    </form>
  )
}

export default EditInstallationDetails
